import { z } from 'zod';

/** ADR-005 alphabet — excludes 0/O/1/I/L */
export const INVITE_CODE_REGEX = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;

export type LeagueType = 'public' | 'private';
export type LeagueRole = 'owner' | 'admin' | 'member';

export const leagueTypeSchema = z.enum(['public', 'private']);
export const leagueRoleSchema = z.enum(['owner', 'admin', 'member']);

export function envelopeSchema<T extends z.ZodTypeAny>(dataSchema: T): z.ZodObject<{ success: z.ZodLiteral<true>; data: T }> {
  return z.object({
    success: z.literal(true),
    data: dataSchema,
  });
}

const duelPlayerSnippetSchema = z
  .object({
    id: z.string(),
    web_name: z.string(),
  })
  .nullable();

export const leagueSchema = z.object({
  id: z.string().uuid(),
  owner_user_id: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  emoji: z.string().nullable(),
  type: leagueTypeSchema,
  show_league_split_on_duels: z.boolean(),
  country: z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  is_system: z.boolean().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  is_member: z.boolean(),
  viewer_role: leagueRoleSchema.nullable(),
  invite_code: z.string().optional(),
  member_count: z.coerce.number().int().nonnegative().optional(),
});

export type League = z.infer<typeof leagueSchema>;

export const myLeagueItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  emoji: z.string().nullable(),
  type: leagueTypeSchema,
  show_league_split_on_duels: z.boolean(),
  country: z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  is_system: z.boolean().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  member_count: z.coerce.number().int().nonnegative(),
  active_this_gw: z.coerce.number().int().nonnegative(),
  user_rank: z.coerce.number().int().nonnegative(),
  user_predictor_rating: z.coerce.number(),
  user_rank_movement: z.coerce.number().int(),
});

export type MyLeagueItem = z.infer<typeof myLeagueItemSchema>;

export const publicLeagueItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  emoji: z.string().nullable(),
  member_count: z.number().int().nonnegative(),
  active_this_gw: z.number().int().nonnegative(),
  is_member: z.boolean(),
  /** Defensive; API public list already excludes system leagues */
  is_system: z.boolean().optional(),
});

export type PublicLeagueItem = z.infer<typeof publicLeagueItemSchema>;

export const paginationSchema = z.object({
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  total_count: z.number().int().nonnegative(),
});

export type Pagination = z.infer<typeof paginationSchema>;

export const gwSummaryDuelsPaginationSchema = paginationSchema.extend({
  has_more: z.boolean(),
});

export type GwSummaryDuelsPagination = z.infer<typeof gwSummaryDuelsPaginationSchema>;

export const leagueMemberSchema = z.object({
  user_id: z.string().uuid(),
  username: z.string(),
  avatar_url: z.string().nullable(),
  role: leagueRoleSchema,
  joined_at: z.string(),
  predictor_rating: z.number(),
  rank_movement: z.number().int(),
  success_rate: z.number(),
  country: z.string().nullable(),
  twitter_handle: z.string().nullable(),
  standing_rank: z.coerce.number().int().positive(),
});

export type LeagueMember = z.infer<typeof leagueMemberSchema>;

export const leagueDuelPickSchema = z.object({
  user_id: z.string().uuid(),
  username: z.string(),
  avatar_url: z.string().nullable(),
  predictor_rating: z.number(),
  pick: z.enum(['player_a', 'player_b']).nullable(),
  is_me: z.boolean(),
});

export type LeagueDuelPick = z.infer<typeof leagueDuelPickSchema>;

export type LeagueDuelPicksTab = 'player_a' | 'player_b' | 'not_voted';

export const gwSummaryDuelSchema = z.object({
  duel_id: z.string().uuid(),
  slug: z.string(),
  player_a: duelPlayerSnippetSchema,
  player_b: duelPlayerSnippetSchema,
  deadline: z.string(),
  league_pct1: z.number(),
  league_pct2: z.number(),
  league_p1_votes: z.number().int().nonnegative(),
  league_p2_votes: z.number().int().nonnegative(),
  league_total_voted: z.number().int().nonnegative(),
  total_members: z.number().int().nonnegative(),
  crowd_pct1: z.number(),
  crowd_pct2: z.number(),
  against_crowd: z.boolean(),
  no_league_votes: z.boolean(),
  user_pick: z.enum(['player_a', 'player_b']).nullable(),
});

export type GwSummaryDuel = z.infer<typeof gwSummaryDuelSchema>;

export const leagueGwSummarySchema = z.object({
  summary: z.object({
    total_members: z.number().int().nonnegative(),
    members_voted: z.number().int().nonnegative(),
    against_crowd_count: z.number().int().nonnegative(),
    total_duels: z.number().int().nonnegative(),
  }),
  your_votes: z.object({
    voted: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
  duels: z.array(gwSummaryDuelSchema),
  duels_pagination: gwSummaryDuelsPaginationSchema.optional(),
});

export type LeagueGwSummary = z.infer<typeof leagueGwSummarySchema>;

export const createLeagueBodySchema = z.object({
  name: z.string().min(1).max(40),
  type: leagueTypeSchema,
  emoji: z.string().max(8).optional(),
  description: z.string().max(5000).optional(),
});

export type CreateLeagueBody = z.infer<typeof createLeagueBodySchema>;

export const updateLeagueBodySchema = z
  .object({
    name: z.string().min(1).max(40).optional(),
    description: z.union([z.string().max(5000), z.null()]).optional(),
    emoji: z.union([z.string().max(8), z.null()]).optional(),
    show_league_split_on_duels: z.boolean().optional(),
    type: leagueTypeSchema.optional(),
  })
  .refine(
    patch =>
      patch.name !== undefined ||
      patch.description !== undefined ||
      patch.emoji !== undefined ||
      patch.show_league_split_on_duels !== undefined ||
      patch.type !== undefined,
    { message: 'At least one field is required' },
  );

export type UpdateLeagueBody = z.infer<typeof updateLeagueBodySchema>;

export type JoinByCodeBody = { code: string };

export type LeagueMembersQuery = {
  limit?: number;
  offset?: number;
};

export type PublicLeaguesQuery = {
  search?: string;
  limit?: number;
  offset?: number;
};

export type LeagueGwSummaryQuery = {
  gameweek_id?: string;
  limit?: number;
  offset?: number;
};
